import './materials.css';
import { useParams } from "react-router-dom";
import { get, getBlob } from "../../services/api";
import { useUI } from "../../UIContext";
import { handleErr } from "../../utils/functions";
import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import worker from "pdfjs-dist/build/pdf.worker.min.js";

pdfjsLib.GlobalWorkerOptions.workerSrc = worker;

function Material() {
    const { id } = useParams();
    const { setMessage, setLoading } = useUI();
    const [moduleName, setModuleName] = useState('');
    const [pdfDocument, setPdfDocument] = useState(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const viewerRef = useRef(null);
    const waitingForRenderRef = useRef(false);
    const renderVersionRef = useRef(0);
    const resizeFrameRef = useRef(null);

    useEffect(() => {
        function syncContainerWidth() {
            if (resizeFrameRef.current) {
                cancelAnimationFrame(resizeFrameRef.current);
            }

            resizeFrameRef.current = requestAnimationFrame(() => {
                if (!viewerRef.current) return;
                setContainerWidth(viewerRef.current.clientWidth);
            });
        }

        syncContainerWidth();

        const resizeObserver = new ResizeObserver(syncContainerWidth);
        if (viewerRef.current) {
            resizeObserver.observe(viewerRef.current);
        }

        window.addEventListener('resize', syncContainerWidth);

        return () => {
            if (resizeFrameRef.current) {
                cancelAnimationFrame(resizeFrameRef.current);
            }

            resizeObserver.disconnect();
            window.removeEventListener('resize', syncContainerWidth);
        };
    }, []);

    useEffect(() => {
        let active = true;

        async function loadMaterial() {
            setLoading(true);
            setModuleName('');
            waitingForRenderRef.current = false;
            setPdfDocument(null);

            await get(
                `module/${id}`,
                true,
                setMessage,
                (response) => {
                    if (!active) {
                        return;
                    }

                    const moduleData = response?.data?.module || response?.data;
                    const loadedName = typeof moduleData?.name === 'string'
                        ? moduleData.name.trim()
                        : '';

                    setModuleName(loadedName);
                },
                () => {},
                () => {},
                'redirect',
                'redirect',
                'unauthorized'
            );
    
            await getBlob(
                `module/${id}/material`,
                true,
                setMessage,
                async (response) => {
                    const blob = response.data;
                    const arrayBuffer = await blob.arrayBuffer();
                    const documentTask = pdfjsLib.getDocument({ data: arrayBuffer });
                    const document = await documentTask.promise;

                    if (!active) {
                        document.destroy();
                        return;
                    }

                    waitingForRenderRef.current = true;
                    setPdfDocument(document);
                },
                (err) => {
                    handleErr(setMessage, `Não foi possível carregar o material: ${err}`);
                },
                () => {
                    if (!waitingForRenderRef.current) {
                        setLoading(false);
                    }
                },
                'redirect',
                'redirect',
                'unauthorized'
            );
        }

        loadMaterial();

        return () => {
            active = false;

            waitingForRenderRef.current = false;
            setLoading(false);
        };
    }, [setLoading, setMessage, id]);

    useEffect(() => {
        return () => {
            if (pdfDocument) {
                pdfDocument.destroy();
            }
        };
    }, [pdfDocument]);

    useEffect(() => {
        if (!pdfDocument || !viewerRef.current || !containerWidth) {
            return;
        }

        let cancelled = false;
        const currentRenderVersion = ++renderVersionRef.current;

        async function renderPdfPages() {
            setLoading(true);

            try {
                const pagesFragment = document.createDocumentFragment();
                const outputScale = window.devicePixelRatio || 1;

                for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber += 1) {
                    if (cancelled || currentRenderVersion !== renderVersionRef.current) {
                        return;
                    }

                    const page = await pdfDocument.getPage(pageNumber);
                    const initialViewport = page.getViewport({ scale: 1 });
                    const fitScale = containerWidth / initialViewport.width;
                    const viewport = page.getViewport({ scale: fitScale });

                    const canvas = document.createElement('canvas');
                    canvas.className = 'material-pdf-canvas';
                    canvas.style.width = `${Math.floor(viewport.width)}px`;
                    canvas.style.height = `${Math.floor(viewport.height)}px`;
                    canvas.width = Math.floor(viewport.width * outputScale);
                    canvas.height = Math.floor(viewport.height * outputScale);

                    const canvasContext = canvas.getContext('2d', { alpha: false });
                    if (!canvasContext) {
                        continue;
                    }

                    canvasContext.setTransform(outputScale, 0, 0, outputScale, 0, 0);

                    const renderTask = page.render({
                        canvasContext,
                        viewport
                    });

                    await renderTask.promise;

                    const pageContainer = document.createElement('div');
                    pageContainer.className = 'material-pdf-page';
                    pageContainer.appendChild(canvas);
                    pagesFragment.appendChild(pageContainer);
                }

                if (cancelled || currentRenderVersion !== renderVersionRef.current || !viewerRef.current) {
                    return;
                }

                viewerRef.current.replaceChildren(pagesFragment);
            } catch (err) {
                handleErr(setMessage, `Não foi possível renderizar o material: ${err}`);
            } finally {
                if (!cancelled && currentRenderVersion === renderVersionRef.current) {
                    waitingForRenderRef.current = false;
                    setLoading(false);
                }
            }
        }

        renderPdfPages();

        return () => {
            cancelled = true;
        };
    }, [pdfDocument, containerWidth, setLoading, setMessage]);

    return (
        <section className='main-container-material'>
            <div className='glass-bg-full-page'></div>

            <div className='material-content-shell'>
                <div className='material-headline'>
                    <h2>{moduleName ? `Material da Aula - ${moduleName}` : 'Material da Aula'}</h2>
                </div>

                <div className='material-viewer-frame'>
                    <div
                        className='material-pdf-viewer'
                        ref={viewerRef}
                        role='document'
                        aria-label='Material de apoio em PDF'
                    />
                </div>
            </div>
        </section>
    );
}

export default Material;